import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIsContentPromotionEnabled } from '../../../../../utils/useIsContentPromotionEnabled';
import { useTranslation } from '../../../../../utils/useTranslation';
import { SmartDrawer } from '../../../../feedback/SmartDrawer';
import { IconHelp } from '../../../../icon/IconHelp';
import { Button } from '../../../../input/Button';
import { Checkbox } from '../../../../input/Checkbox';
import { Box } from '../../../../layout/Box';
import { DataTestId } from '../../../../layout/DataTestIdWrapper';
import { Flex } from '../../../../layout/Flex';
import { Typography } from '../../../../typography/Typography';
import { ADDITIONAL_FIELDS_MAPPING } from '../../constants';
import { ContentColumn, ControlColumn, DateColumn, Row } from '../../styles';
import { FeedManagementAdditionalField } from '../../types';
import { FeedManagementHeaderProps } from './types';

export const FeedManagementHeader = (props: FeedManagementHeaderProps) => {
    const { selected, onSelect, additionalFields, ...other } = props;
    const onToggleSelected = useCallback(() => onSelect(!selected), [selected, onSelect]);
    const t = useTranslation();
    const contentPromotionEnabled =
        useIsContentPromotionEnabled() &&
        additionalFields?.[FeedManagementAdditionalField.PROMOTION];
    const [isPromotionModalOpen, setIsPromotionModalOpen] = useState(false);

    const handleOpenPromotionModal = useCallback(() => {
        setIsPromotionModalOpen(true);
    }, []);

    const handleClosePromotionModal = useCallback(() => {
        setIsPromotionModalOpen(false);
    }, []);

    return (
        <Row {...other} py={0} height={56}>
            <ControlColumn>
                <Checkbox checked={selected} onChange={onToggleSelected} />
            </ControlColumn>
            <Flex width={48} flexShrink={0}>
                <Typography variant="caption" color="secondary">
                    {t('Статус')}
                </Typography>
            </Flex>
            <Flex width={64} flexShrink={0}>
                <Typography variant="caption" color="secondary">
                    {t('Контент')}
                </Typography>
            </Flex>
            <Flex width={70} flexShrink={0}>
                <Typography variant="caption" color="secondary" mx={1}>
                    {t('Тип')}
                </Typography>
            </Flex>
            <ContentColumn alignSelf="unset">
                <Typography variant="caption" color="secondary">
                    {t('Описание')}
                </Typography>
            </ContentColumn>
            <ContentColumn
                width={74}
                alignItems="flex-end"
                alignSelf="unset"
                flexGrow={0}
                flexShrink={0}
            >
                <Typography variant="caption" color="secondary">
                    {t('Показы в ленте')}
                </Typography>
            </ContentColumn>
            {additionalFields?.[FeedManagementAdditionalField.SHOWS] ? (
                <ContentColumn
                    width={74}
                    alignItems="flex-end"
                    alignSelf="unset"
                    flexGrow={0}
                    flexShrink={0}
                >
                    <Typography variant="caption" color="secondary">
                        {ADDITIONAL_FIELDS_MAPPING[FeedManagementAdditionalField.SHOWS]}
                    </Typography>
                </ContentColumn>
            ) : null}
            {additionalFields?.[FeedManagementAdditionalField.COVERAGE] ? (
                <ContentColumn
                    width={66}
                    alignItems="flex-end"
                    alignSelf="unset"
                    flexGrow={0}
                    flexShrink={0}
                >
                    <Typography variant="caption" color="secondary">
                        {ADDITIONAL_FIELDS_MAPPING[FeedManagementAdditionalField.COVERAGE]}
                    </Typography>
                </ContentColumn>
            ) : null}
            {additionalFields?.[FeedManagementAdditionalField.ACCOUNTS_INVOLVED] ? (
                <ContentColumn
                    width={108}
                    alignItems="flex-end"
                    alignSelf="unset"
                    flexGrow={0}
                    flexShrink={0}
                >
                    <Typography variant="caption" color="secondary">
                        {ADDITIONAL_FIELDS_MAPPING[FeedManagementAdditionalField.ACCOUNTS_INVOLVED]}
                    </Typography>
                </ContentColumn>
            ) : null}
            {additionalFields?.[FeedManagementAdditionalField.SUBSCRIBERS] ? (
                <ContentColumn
                    width={101}
                    alignItems="flex-end"
                    alignSelf="unset"
                    flexGrow={0}
                    flexShrink={0}
                >
                    <Typography variant="caption" color="secondary">
                        {ADDITIONAL_FIELDS_MAPPING[FeedManagementAdditionalField.SUBSCRIBERS]}
                    </Typography>
                </ContentColumn>
            ) : null}
            <ContentColumn
                width={127}
                alignItems="flex-end"
                alignSelf="unset"
                flexGrow={0}
                flexShrink={0}
            >
                <Typography variant="caption" color="secondary">
                    {t('Взаимодействия')}
                </Typography>
            </ContentColumn>
            {contentPromotionEnabled ? (
                <Flex
                    width={100}
                    justifyContent="center"
                    alignItems="center"
                    direction="row"
                    spacing={0.5}
                >
                    <Typography variant="caption" color="secondary">
                        {ADDITIONAL_FIELDS_MAPPING[FeedManagementAdditionalField.PROMOTION]}
                    </Typography>
                    <IconHelp
                        color="var(--color-secondary)"
                        cursor="pointer"
                        size={16}
                        dataTestId={DataTestId.FEED_MANAGEMENT_PROMOTION_HELP}
                        onClick={handleOpenPromotionModal}
                    />
                </Flex>
            ) : null}
            <DateColumn>
                <Typography variant="caption" color="secondary">
                    {t('Опубликован')}
                </Typography>
            </DateColumn>
            <ControlColumn />
            {isPromotionModalOpen
                ? createPortal(
                      <SmartDrawer
                          dataTestId={DataTestId.FEED_MANAGEMENT_PROMOTION_DRAWER}
                          onClose={handleClosePromotionModal}
                          titleContent={
                              <Typography variant="title3" weight="bold">
                                  {t('Продвижение')}
                              </Typography>
                          }
                          mainContent={(closeHandler) => (
                              <Flex direction="column" spacing={2}>
                                  <Typography variant="body2">
                                      {t(
                                          'Все выбранные посты, опубликованные за 90 дней, могут получить платное продвижение. Для этого необходимо, чтобы в Директе были активные объявления в кампаниях на РСЯ с урлами как на постах в Ритме. Будут использоваться те же таргетинги и та же стратегия, что у кампаний в Директе. Можно исключить посты, которые вы не хотите продвигать в смешанных с РСЯ кампаниях. При необходимости полностью отключить платное продвижение в смешанных кампаниях с РСЯ нужно перейти в Директ и исключить площадку "Продвижение постов в Ритме" в кампаниях РСЯ. Вы так же можете создать отдельную кампанию только для продвижения постов Ритма без РСЯ, для этого перейдите в "Начать продвижение".',
                                      )}
                                  </Typography>
                                  <Box pt={1} width="100%">
                                      <Button size="l" width="100%" onClick={closeHandler}>
                                          {t('Хорошо')}
                                      </Button>
                                  </Box>
                              </Flex>
                          )}
                      />,
                      document.body,
                  )
                : null}
        </Row>
    );
};
